import { statusLabel, awardLabel } from "../components/status-badge.js";

// Filter/facet model shared by the media lists. A filter is { type, value } with
// type ∈ status (engagement) | ownership | award | genre | platform | decade.
// status/ownership/award are pickable from the list's "+ Filter" panel; genre/
// platform/decade can arrive pre-set from a Stats deep-link (e.g. /games?genre=RPG)
// and render as removable chips, though they're not offered in the panel.

export const FACET_TYPES = [
	"status",
	"ownership",
	"genre",
	"decade",
	"platform",
	"award",
	// Items you corrected by hand (Edit details / re-link) — roadmap #16.
	"edited",
	// Work by someone you follow (value = person_key) — People Phase 3.
	"person",
	// Your own tags (roadmap #33).
	"tag",
	// TV / anime still on the air (roadmap #43): "now" | "running".
	"airing",
];

export const FACET_TYPE_LABELS = {
	status: "Status",
	ownership: "Ownership",
	genre: "Genre",
	decade: "Decade",
	platform: "Platform",
	award: "Award",
	edited: "Edited",
	person: "Person",
	tag: "Tag",
	airing: "Airing",
};

// ---- the "airing" facet (TV / anime), from the local episode catalogs ----
// now     — episodes aired or airing within two weeks either side of today
// running — TVmaze still lists the show as running (between seasons too)
const airingFacets = { now: new Set(), running: new Set() };

export function setAiringFacets({ now = [], running = [] } = {}) {
	airingFacets.now = new Set(now.map(Number));
	airingFacets.running = new Set(running.map(Number));
}

export function airingFacetOptions(mediaType) {
	if (mediaType !== "tv" && mediaType !== "anime") return [];
	return [
		{ value: "now", label: "Airing now" },
		{ value: "running", label: "Still running" },
	];
}

export function isAiring(item, which = "now") {
	return item?.tvmaze_id != null && (airingFacets[which]?.has(Number(item.tvmaze_id)) ?? false);
}

// ---- the "tag" facet: the tags in use per category (db/media.js loadTagFacets) ----
const tagFacets = new Map(); // media_type → [tag] (most used first)

export function setTagFacets(byMedia) {
	tagFacets.clear();
	for (const [media, tags] of Object.entries(byMedia ?? {})) tagFacets.set(media, tags);
}

export function tagFacetOptions(mediaType) {
	return (tagFacets.get(mediaType) ?? []).map((t) => ({ value: t, label: `#${t}` }));
}

// Tag suggestions on a detail page: this category's tags first, then the rest.
export function tagSuggestions(mediaType) {
	const own = tagFacets.get(mediaType) ?? [];
	const rest = [...tagFacets.entries()].filter(([m]) => m !== mediaType).flatMap(([, t]) => t);
	return [...new Set([...own, ...rest])].slice(0, 40);
}

// ---- the "person" facet ----------------------------------------------------
// A followed person's known works (seen_work_ids, kept fresh by the weekly
// "New from …" check) say which library items are theirs — matched on the id
// field of that person's category. Loaded by db/people.js loadPersonFacets().
const PERSON_ID_FIELD = { videogame: "igdb_id", movie: "imdb_id", tv: "tvmaze_id", book: "ol_id", anime: "mal_id" };
const personFacets = new Map(); // person_key → { name, media_type, ids: Set }

export function setPersonFacets(people) {
	personFacets.clear();
	for (const p of people ?? []) {
		if (!PERSON_ID_FIELD[p.media_type] || !p.seen_work_ids?.length) continue;
		personFacets.set(p.person_key, { name: p.name, media_type: p.media_type, ids: new Set(p.seen_work_ids.map(String)) });
	}
}

// The followed people a category's filter panel can offer.
export function personFacetOptions(mediaType) {
	return [...personFacets.entries()]
		.filter(([, p]) => p.media_type === mediaType)
		.map(([key, p]) => ({ value: key, label: p.name }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

function matchesPerson(item, key) {
	const p = personFacets.get(key);
	if (!p) return false;
	const id = item[PERSON_ID_FIELD[p.media_type]];
	return id != null && p.ids.has(String(id));
}

// Read any facet params from a URLSearchParams into an array of { type, value }.
export function parseFilters(params) {
	const out = [];
	for (const type of FACET_TYPES) {
		const value = params.get(type);
		if (value != null && value !== "") {
			out.push({ type, value: type === "decade" ? Number(value) : value });
		}
	}
	return out;
}

// Does one item satisfy a single { type, value } facet? Works for games and
// books; a type an item's media doesn't carry (e.g. platform on a book) simply
// doesn't match.
export function matchesFacet(item, facet) {
	switch (facet.type) {
		case "status":
			return item.engagement_status === facet.value;
		case "ownership":
			return item.ownership_status === facet.value;
		case "genre":
			return (item.genres ?? []).includes(facet.value);
		case "platform":
			return (item.owned_on ?? []).includes(facet.value);
		case "award":
			return (item.awards ?? []).includes(facet.value);
		case "edited":
			return Object.keys(item.source_values ?? {}).length > 0;
		case "person":
			return matchesPerson(item, facet.value);
		case "tag":
			return (item.tags ?? []).includes(facet.value);
		case "airing":
			return isAiring(item, facet.value);
		case "decade":
			return (
				item.release_year != null &&
				Math.floor(item.release_year / 10) * 10 === facet.value
			);
		default:
			return true;
	}
}

// Apply a set of active filters: OR within a type (any selected status matches),
// AND across types (must satisfy each type that has selections).
export function matchesAllFilters(item, filters) {
	if (!filters || filters.length === 0) return true;
	const byType = new Map();
	for (const f of filters) {
		if (!byType.has(f.type)) byType.set(f.type, []);
		byType.get(f.type).push(f);
	}
	for (const group of byType.values()) {
		if (!group.some((f) => matchesFacet(item, f))) return false;
	}
	return true;
}

// Human label for a facet's value (media-aware for status/ownership).
export function facetValueLabel(facet, mediaType) {
	if (facet.type === "status" || facet.type === "ownership") {
		return statusLabel(facet.value, mediaType);
	}
	if (facet.type === "award") return awardLabel(facet.value);
	if (facet.type === "decade") return `${facet.value}s`;
	if (facet.type === "edited") return "Edited by you";
	if (facet.type === "tag") return `#${facet.value}`;
	if (facet.type === "airing") return facet.value === "running" ? "Still running" : "Airing now";
	if (facet.type === "person") return personFacets.get(facet.value)?.name ?? "Someone you follow";
	return facet.value;
}
